import type { Desktop, DesktopPlatform, DesktopSystem } from '@boardflight/shared';

/**
 * "Is the desktop app here?" — the question a page cannot put to the operating system
 * (`docs/off-board-plan.md` §2.3).
 *
 * It is answered from three signals, in this order, and the order is the design:
 *
 * 1. **A desktop app of this account is running.** The account says so (`GET /v1/me/desktops`), and
 *    it is the only one of the three that is a fact. An install shows its progress as it happens.
 * 2. **This browser has been opened by the desktop app.** The app leaves a mark when it sends
 *    somebody here — to approve its sign-in on `/link`, or back from a deep link — so the app is on
 *    this computer, running or not. An install is handed to it by a `boardflight://` link.
 * 3. **The account has had a desktop app on this kind of computer.** A guess, and the weakest: it
 *    may be a different Mac. Still worth the same deep link, because the alternative is telling
 *    somebody who has the app to go and open it.
 *
 * None of the three is "no": it is "nothing says so", and the page then says only what is true
 * whichever it is — when the install will run.
 *
 * Pure, apart from the two functions at the end that read and write the mark.
 */

export type DesktopHere =
  /** One is running now. */
  | 'online'
  /** This browser carries the mark. */
  | 'this-browser'
  /** The account has one on this platform. */
  | 'this-platform'
  | 'none';

export function desktopHere(facts: {
  desktops: readonly Pick<Desktop, 'online' | 'platform'>[];
  /** {@link desktopMarked}. */
  mark: boolean;
  /** {@link platformOf} this browser, or null for a phone or anything unrecognised. */
  platform: DesktopPlatform | null;
}): DesktopHere {
  if (facts.desktops.some((desktop) => desktop.online)) return 'online';
  if (facts.mark) return 'this-browser';
  if (facts.platform !== null && facts.desktops.some((desktop) => desktop.platform === facts.platform)) return 'this-platform';
  return 'none';
}

/** As much of `navigator` as says what kind of computer this is. */
export interface NavigatorLike {
  userAgentData?: { platform?: string; mobile?: boolean } | null;
  platform?: string;
  userAgent?: string;
  maxTouchPoints?: number;
}

/**
 * Which of the three desktop systems this browser is on, or null.
 *
 * Null for every phone and tablet, deliberately: Android calls itself Linux and an iPad calls
 * itself a Mac, and neither can run the desktop app — a guess that said otherwise would fire a deep
 * link at a device with nothing to open it.
 */
export function platformOf(nav: NavigatorLike | null | undefined): DesktopSystem | null {
  if (!nav) return null;
  const agent = nav.userAgent ?? '';
  if (nav.userAgentData?.mobile === true || /Android|iPhone|iPad|iPod|Mobile|CrOS/i.test(agent)) return null;
  const name = (nav.userAgentData?.platform ?? nav.platform ?? '').toLowerCase();
  // An iPad asking for the desktop site: a Mac by name, with a touch screen no Mac has.
  if (name.startsWith('mac')) return (nav.maxTouchPoints ?? 0) > 1 ? null : 'macos';
  if (name.startsWith('win')) return 'windows';
  if (name.includes('linux') || name.includes('x11')) return 'linux';
  return null;
}

/* ------------------------------------------------------------------------------ the mark */

/** The key §2.3 names. Its value is when it was left; nothing reads more than its presence. */
export const DESKTOP_HERE_KEY = 'bf.desktop.here';

/**
 * The query parameter the desktop app adds to a storefront address it opens in the browser — a
 * deep link's return. Read once, then taken out of the address (`takeDesktopReturn`).
 */
export const DESKTOP_RETURN_PARAM = 'bf-here';

function browserStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** Leave the mark: the desktop app sent somebody to this browser. */
export function markDesktopHere(storage: Pick<Storage, 'setItem'> | null = browserStorage(), now: number = Date.now()): void {
  try {
    storage?.setItem(DESKTOP_HERE_KEY, String(now));
  } catch {
    // A browser that keeps nothing is asked to open the app by hand; that is all it costs.
  }
}

export function desktopMarked(storage: Pick<Storage, 'getItem'> | null = browserStorage()): boolean {
  try {
    return Boolean(storage?.getItem(DESKTOP_HERE_KEY));
  } catch {
    return false;
  }
}

/**
 * Whether a query string says the desktop app opened this page, and the same query string without
 * the parameter that said so — it is a note to this page, not part of anybody's address.
 */
export function takeDesktopReturn(search: string): { returned: boolean; search: string } {
  const params = new URLSearchParams(search);
  if (!params.has(DESKTOP_RETURN_PARAM)) return { returned: false, search };
  params.delete(DESKTOP_RETURN_PARAM);
  const rest = params.toString();
  return { returned: true, search: rest ? `?${rest}` : '' };
}
